/**
 * useAutoRefreshAssessments Hook
 * Provides automatic refresh of assessment data with multiple strategies:
 * 1. Event-based updates when assessments are completed
 * 2. Periodic polling while page is visible
 * 3. Refresh when browser tab becomes visible again
 */

import { useEffect, useRef, useCallback } from 'react';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface UseAutoRefreshAssessmentsOptions {
  pollingIntervalMs?: number; // Interval for periodic polling (default: 5000ms = 5 seconds)
  enabled?: boolean; // Whether to enable auto-refresh (default: true)
  onRefresh?: () => void | Promise<void>; // Callback to trigger refresh
}

export const useAutoRefreshAssessments = (
  options: UseAutoRefreshAssessmentsOptions = {}
): void => {
  const {
    pollingIntervalMs = 5000,
    enabled = true,
    onRefresh
  } = options;

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);
  const lastRefreshTimeRef = useRef<number>(Date.now());
  const minRefreshIntervalMs = 2000; // Minimum time between refreshes to avoid excessive API calls

  // Callback to perform refresh with debouncing
  const performRefresh = useCallback(async () => {
    const now = Date.now();
    
    // Debounce: don't refresh too frequently
    if (now - lastRefreshTimeRef.current < minRefreshIntervalMs) {
      return;
    }

    lastRefreshTimeRef.current = now;

    if (onRefresh) {
      try {
        await onRefresh();

      } catch (error) {

      }
    }
  }, [onRefresh]);

  // Handle visibility change (when user switches tabs)
  const handleVisibilityChange = useCallback(() => {
    if (!enabled) return;

    if (!document.hidden) {
      // Page became visible again - refresh immediately

      performRefresh();
    }
  }, [enabled, performRefresh]);

  // Set up event listeners and polling
  useEffect(() => {
    if (!enabled || !onRefresh) {
      return;
    }

    // Only initialize once
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    // 1. Listen for assessment completion events
    const unsubscribeAssessmentUpdate = assessmentUpdateNotifier.subscribe((assessmentType) => {

      performRefresh();
    });

    // 2. Listen for visibility changes (tab switches, minimizing window, etc.)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 3. Start periodic polling interval
    pollingIntervalRef.current = setInterval(() => {
      if (!document.hidden) {
        // Only poll if page is visible to avoid unnecessary API calls

        performRefresh();
      }
    }, pollingIntervalMs);

    // Cleanup function
    return () => {
      isInitializedRef.current = false;
      
      // Unsubscribe from assessment updates
      unsubscribeAssessmentUpdate();

      // Remove visibility change listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Clear polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [enabled, onRefresh, handleVisibilityChange, performRefresh, pollingIntervalMs]);
};


